import { useEffect, useMemo, useState } from "react";
import { Eyebrow, Headline } from "@/components/Editorial";
import { Spinner } from "@/components/Loader";
import CopyButton from "@/components/affiliate/CopyButton";
import FilterChips from "@/components/affiliate/FilterChips";
import useProgramFromQuery from "@/lib/useProgramFromQuery";
import { affGet } from "@/lib/affiliateApi";
import { C } from "@/lib/brand";

// Hooks & conversation starters. Each hook is a short opener the
// affiliate can pluck, adapt, and post.
//
// API contract:
//   GET /affiliate/content/hooks →
//     { hooks: [{id, text, programs: [], categories: []}] }
//
// Category labels are program-specific. We merge labels from every
// program we ship content for; unrecognised slugs fall back to a
// title-cased version of the enum (handled below in the render
// branch) so a new program can ship without an FE change.
const categoryLabels = {
  // Recovery & Healing
  whole_body: "WHOLE BODY",
  skin_collagen: "SKIN & COLLAGEN",
  recovery_injury: "RECOVERY & INJURY",
  gut_healing: "GUT HEALING",
  personal_honest: "PERSONAL & HONEST",
  comparison_education: "COMPARISON & EDUCATION",
  // Weight Management
  honest_reality: "HONEST REALITY",
  brand_name_awareness: "BRAND-NAME AWARENESS",
  clinical_infrastructure: "CLINICAL INFRASTRUCTURE",
  patient_progress: "PATIENT PROGRESS",
  personal_vulnerable: "PERSONAL & VULNERABLE",
  category_education: "CATEGORY EDUCATION",
};

export default function AssetsHooks() {
  const [hooks, setHooks] = useState(null);
  const [program, setProgram] = useProgramFromQuery();
  const [category, setCategory] = useState("all");
  const [query, setQuery] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await affGet("/affiliate/content/hooks", { auth: true });
        // BE returns hooks pre-bucketed by category as `byCategory`,
        // plus an `items` flat array of the same rows for convenience.
        // Prefer the flat array — keeps our filter chips logic simple.
        let flat = [];
        if (Array.isArray(res?.items)) {
          flat = res.items;
        } else if (Array.isArray(res?.hooks)) {
          flat = res.hooks;
        } else if (res?.byCategory && typeof res.byCategory === "object") {
          for (const [cat, rows] of Object.entries(res.byCategory)) {
            for (const r of (rows || [])) {
              flat.push({ ...r, categories: r.categories || [cat] });
            }
          }
        }
        const normalized = flat.map((h) => ({
          id: h.id || h._id || h.contentId,
          // Hooks live in `body` on the BE content row, sometimes in
          // `text` on older shapes — accept both.
          text: h.body || h.text || "",
          programs: h.programs || (h.programSlug ? [h.programSlug] : []),
          categories: h.categories || (h.category ? [h.category] : []),
        })).filter((h) => h.text);
        if (!cancelled) setHooks(normalized);
      } catch {
        if (!cancelled) setHooks([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const programs = useMemo(() => {
    if (!hooks) return [];
    const set = new Set();
    hooks.forEach((h) => (h.programs || []).forEach((p) => set.add(p)));
    return Array.from(set);
  }, [hooks]);
  const categories = useMemo(() => {
    if (!hooks) return [];
    const set = new Set();
    hooks.forEach((h) => (h.categories || []).forEach((c) => set.add(c)));
    return Array.from(set);
  }, [hooks]);

  const visible = useMemo(() => {
    if (!hooks) return [];
    const q = query.trim().toLowerCase();
    return hooks.filter((h) => {
      if (program !== "all" && !(h.programs || []).includes(program)) return false;
      if (category !== "all" && !(h.categories || []).includes(category)) return false;
      if (q && !h.text.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [hooks, program, category, query]);

  if (hooks === null) {
    return <div style={{ padding: 64, textAlign: "center" }}><Spinner size={28} /></div>;
  }

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "40px 24px 80px" }}>
      <Eyebrow color={C.peachDeep}>★ QUICK REFERENCE</Eyebrow>
      <Headline before="HOOKS &" italic="starters." size={48} className="mt-4" />
      <p style={{ marginTop: 14, fontSize: 15, color: C.warmGray, maxWidth: 640, lineHeight: 1.6 }}>
        Short, tested openers. Pluck one, adapt the wording, and you've got the first line of a post.
      </p>

      <div style={{ marginTop: 28, display: "flex", flexDirection: "column", gap: 14 }}>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search hooks…"
          data-testid="hooks-search"
          style={{ padding: "12px 14px", fontSize: 14, background: C.pure, border: `1px solid ${C.warmGray3}`, outline: "none" }}
        />
        {programs.length > 1 && (
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", color: C.warmGray, marginBottom: 8 }}>PROGRAM</div>
            <FilterChips value={program} onChange={setProgram} options={programs} testIdPrefix="hooks-program" />
          </div>
        )}
        {categories.length > 1 && (
          <div>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.22em", color: C.warmGray, marginBottom: 8 }}>CATEGORY</div>
            <FilterChips
              value={category}
              onChange={setCategory}
              options={categories.map((c) => ({ value: c, label: categoryLabels[c] || c.replace(/_/g, " ").toUpperCase() }))}
              testIdPrefix="hooks-category"
            />
          </div>
        )}
      </div>

      <div data-testid="hooks-list" style={{ marginTop: 28, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 14 }}>
        {visible.length === 0 ? (
          <div data-testid="hooks-empty" style={{ padding: 24, background: C.bone, fontSize: 14, color: C.warmGray, fontFamily: '"Instrument Serif", Georgia, serif', fontStyle: "italic" }}>
            No hooks match these filters. Try resetting or check back as we add more.
          </div>
        ) : (
          visible.map((h) => (
            <article
              key={h.id}
              data-testid={`hook-${h.id}`}
              style={{ background: C.pure, border: `1px solid ${C.warmGray3}`, padding: 18, display: "flex", flexDirection: "column", gap: 12 }}
            >
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.55, color: C.ink }}>
                {h.text}
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                {(h.categories || []).map((c) => (
                  <span key={c} style={{ fontSize: 9, fontWeight: 800, letterSpacing: "0.2em", padding: "2px 6px", background: C.bone, color: C.warmGray }}>
                    {categoryLabels[c] || c.replace(/_/g, " ").toUpperCase()}
                  </span>
                ))}
                <div style={{ flex: 1 }} />
                <CopyButton text={h.text} testId={`hook-copy-${h.id}`} label="COPY" />
              </div>
            </article>
          ))
        )}
      </div>
    </div>
  );
}
